import React, { useEffect, useMemo, useRef, useState } from "react";

import dayjs, { Dayjs } from "dayjs";
import i18next from "i18next";
import { toast } from "react-toastify";

import { useCalculateOrder, useCompleteRealOrder, useGetOrder, useSaveOrder, } from "api/reactQuery/queryComponents";
import {
  BSCoreServicesDtoMarketplaceOrderDto,
  BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse,
  BSCoreWebRequestsResponsesDistributorsDistributorResponse,
  BSDALMarketplaceEnumsOrderStatus,
} from "api/reactQuery/querySchemas";
import Loader from "components/Loader";
import OrderInfoModal from "components/MpOrder/CreateOrder/OrderInfoModal";
import OrderInfoSection from "components/MpOrder/CreateOrder/OrderInfoSection";
import { dateFormatForBE } from "components/Utils/dayjsUtils";
import { MarketplaceOrderStatuses } from "components/Utils/enums";

import SuccessModal from "./SuccessModal";

const CreateOrder: React.FC = () => {
  const [orderData, setOrderData] = useState<BSCoreServicesDtoMarketplaceOrderDto>();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasCalledSaveOrder, setHasCalledSaveOrder] = useState(false);
  // Tracks whether the order had availability overflow when Next was pressed.
  // Read in saveOrder.onSuccess to show the correct modal even when the server
  // strips out-of-stock products and returns New/WaitingCompletion.
  const hadOverflowOnSubmitRef = useRef(false);
  const [step, setStep] = useState(0);
  const [selectedDistributor, setSelectedDistributor] = useState<BSCoreWebRequestsResponsesDistributorsDistributorResponse>();
  const [selectedDepots, setSelectedDepots] = useState<BSCoreWebRequestsResponsesDistributorsDistributorDepotResponse[]>();
  const [selectedDeliveryDate, setSelectedDeliveryDate] = useState<Dayjs>();
  const [isAvailabilityModalOpen, setIsAvailabilityModalOpen] = useState(false);
  const [showAllProductsAvailability, setShowAllProductsAvailability] = useState(false);
  const [showMultipackProductsAvailability, setShowMultipackProductsAvailability,] = useState(false);
  const [showOnInvoiceProductsAvailability, setShowOnInvoiceProductsAvailability,] = useState(false);
  const [showGasProductsAvailability, setShowGasProductsAvailability] = useState(false);

  const [isPreviewOrderModalOpen, setIsPreviewOrderModalOpen] = useState(false);
  const [preparedOrder, setPreparedOrder] = useState<BSCoreServicesDtoMarketplaceOrderDto>();
  const [orderComment, setOrderComment] = useState<string | null>(null);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isOrderCompleted, setIsOrderCompleted] = useState(false);

  const { mutate: getOrder, isPending: initialOrderLoading } = useGetOrder({
    onError: () => toast.error(i18next.t("something_went_wrong")),
    onSuccess: (data) => {
      resetCalculatedData();
      resetSavedOrderResponse();
      checkProductAvailability(data);
      setIsOrderCompleted(false);
      setHasSubmitted(false);
      setHasCalledSaveOrder(false);
      setOrderData(data);
    },
  });

  const {
    mutate: calculateOrder,
    data: calculatedOrderResponse,
    reset: resetCalculatedData,
    isPending: isCalculateOrderLoading,
  } = useCalculateOrder({
    onSuccess: (data) => {
      setOrderData(data);
    },
  });

  const {
    mutate: completeOrder,
    isPending: isLoadingCompleteOrder,
    reset: resetCompleteOrderResponse,
  } = useCompleteRealOrder({
    onSuccess: () => {
      setIsPreviewOrderModalOpen(false);
      // set order status and show success modal
      setIsSuccessModalOpen(true);
      setIsOrderCompleted(true);
    },
  });

  const {
    data: savedOrderResponse,
    mutate: saveOrder,
    isPending: isSaveOrderLoading,
    reset: resetSavedOrderResponse,
  } = useSaveOrder({
    onSuccess: (data) => {
      // Always start the session timer.
      setHasCalledSaveOrder(true);

      setOrderData(data);

      if (hadOverflowOnSubmitRef.current) {
        checkProductAvailability(data);
        setIsAvailabilityModalOpen(true);

        return;
      }

      setPreparedOrder(data);
      checkProductAvailability(data);

      if (data.status !== undefined && data.status !== null) {
        // BE can return New or WaitingCompletion for a valid order ready to complete.
        if (
          MarketplaceOrderStatuses[data.status] === BSDALMarketplaceEnumsOrderStatus.WaitingCompletion ||
          MarketplaceOrderStatuses[data.status] === BSDALMarketplaceEnumsOrderStatus.New
        ) {
          return setIsPreviewOrderModalOpen(true);
        }

        // There are not enough available units. Show error modal.
        else if (MarketplaceOrderStatuses[data?.status] === BSDALMarketplaceEnumsOrderStatus.InProgress) {
          setIsAvailabilityModalOpen(true);
        }
      }
    },
  });

  const shouldShowProductAvailability = (
    orders: BSCoreServicesDtoMarketplaceOrderDto[],
    filterType: "all" | "gas" | "invoice" | "multipack"
  ) => {
    let foundUnavailableProduct = false;

    orders.forEach((order) =>
      order.products
        .filter((product) => {
          if (filterType === "all") {
            return (
              !product.isInvoiceProduct &&
              !product.isMultiPack &&
              product.productTypeId !== 1 &&
              product.productTypeId !== 2
            );
          }

          if (filterType === "invoice") {
            return product.isInvoiceProduct;
          }

          if (filterType === "gas") {
            return product.productTypeId === 1 || product.productTypeId === 2;
          }

          return product.isMultiPack;
        })
        .forEach(({ orderedUnits, currentAvailableUnits }) => {
          if (
            currentAvailableUnits !== undefined &&
            currentAvailableUnits !== null &&
            orderedUnits > currentAvailableUnits
          ) {
            return (foundUnavailableProduct = true);
          }
        })
    );

    return foundUnavailableProduct;
  };

  const checkProductAvailability = (data: BSCoreServicesDtoMarketplaceOrderDto) => {
    if (!data.childOrders) return null;

    setShowAllProductsAvailability(shouldShowProductAvailability(data.childOrders, "all"));

    setShowMultipackProductsAvailability(shouldShowProductAvailability(data.childOrders, "multipack"));

    setShowOnInvoiceProductsAvailability(shouldShowProductAvailability(data.childOrders, "invoice"));

    setShowGasProductsAvailability(shouldShowProductAvailability(data.childOrders, "gas"));
  };


  const resetOrder = () => {
    setIsSuccessModalOpen(false);
    setIsAvailabilityModalOpen(false);
    resetCalculatedData();
    resetCompleteOrderResponse();
    resetSavedOrderResponse();
    setSelectedDeliveryDate(undefined);
    setSelectedDistributor(null);
    setSelectedDepots(null);
    setHasSubmitted(false);
    setHasCalledSaveOrder(false);
    setStep(0);
  };

  // If one of the children is not with status "New", it will be applied to the parent,
  // so looping is not required in this case.
  const isNewOrder = MarketplaceOrderStatuses[orderData?.status] === BSDALMarketplaceEnumsOrderStatus.New;

  const deliveryDate: Dayjs = useMemo(
    () =>
      selectedDeliveryDate
        ? selectedDeliveryDate
        : !isNewOrder
          ? orderData?.deliveryDate && dayjs(orderData?.deliveryDate)
          : undefined,
    [selectedDeliveryDate, isNewOrder, orderData?.deliveryDate]
  );

  // We assume that every product is a physical and takes space or has weight,
  // otherwise we should check each orderData.childOrder for quantities.
  const areQuantitiesSelected = !!orderData?.total?.ttlVolumeInHL || !!orderData?.total?.ttlWeight;

  const isTotalWeightInLimit = orderData?.isTotalWeightInLimit;

  const isTruckAreaFactorInLimit = orderData?.isTruckAreaFactorInLimit;


  const saveAndSendDisabled =
    isSaveOrderLoading ||
    isCalculateOrderLoading ||
    !areQuantitiesSelected ||
    !isTotalWeightInLimit ||
    !isTruckAreaFactorInLimit;

  const handleCompleteOrder = async () => {
    if (!orderData) return;

    completeOrder({
      body: { ...preparedOrder, comment: orderComment },
    });
  };

  const prepareOrder = () => {
    if (!orderData) return;

    // Mark as submitted immediately so rows turn red for any overflow while saveOrder is in flight.
    setHasSubmitted(true);

    hadOverflowOnSubmitRef.current = !!orderData.childOrders?.some((childOrder) =>
      childOrder.products?.some(
        (product) => (product.orderedUnits ?? 0) > (product.currentAvailableUnits ?? 0)),
    );

    const childOrders = orderData?.childOrders?.map((childOrder) => {
      return {
        ...childOrder,
        deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
        products: childOrder.products.filter(({ parentId }) => parentId === null),
      };
    });

    saveOrder({
      body: {
        ...orderData,
        childOrders: childOrders,
        deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
      },
    });
  };

  useEffect(() => {
    if (selectedDepots?.length > 0 && step === 1) {
      getOrder({
        body: {
          deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
          depotIds: selectedDepots?.map((depot) => depot.id),
        },
      });
    }
  }, [selectedDepots, step, deliveryDate]);

  // When the user changes the delivery date while on the products step,
  // recalculate availability against the new date without resetting the
  // entered quantities (calculateOrder preserves orderData.products,
  // unlike getOrder which would refetch and wipe them).
  useEffect(() => {
    if (step === 2 && orderData && deliveryDate) {
      calculateOrder({
        body: {
          ...orderData,
          deliveryDate: dayjs(deliveryDate).format(dateFormatForBE),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryDate]);

  return (
    <>
      <Loader isLoading={isSaveOrderLoading || isCalculateOrderLoading} />

      <OrderInfoSection
        initialOrderLoading={initialOrderLoading}
        resetCalculatedData={resetCalculatedData}
        selectedDepots={selectedDepots}
        setSelectedDepots={setSelectedDepots}
        selectedDistributor={selectedDistributor}
        setSelectedDistributor={setSelectedDistributor}
        deliveryDate={deliveryDate}
        setDeliveryDate={setSelectedDeliveryDate}
        saveAndSendDisabled={saveAndSendDisabled}
        saveAndSendLoading={isSaveOrderLoading || isLoadingCompleteOrder}
        prepareOrder={prepareOrder}
        dateCreated={
          orderData?.dateCreated &&
          dayjs(orderData?.dateCreated)?.add(
            dayjs(orderData?.dateCreated).utcOffset(),
            "minutes"
          )
        }
        isOrderSubmitted={hasSubmitted}
        hasCalledSaveOrder={hasCalledSaveOrder}
        showCountdown={hasCalledSaveOrder && !isOrderCompleted}
        showAllProductsAvailability={showAllProductsAvailability}
        showMultipackProductsAvailability={showMultipackProductsAvailability}
        showOnInvoiceProductsAvailability={showOnInvoiceProductsAvailability}
        showGasProductsAvailability={showGasProductsAvailability}
        enableEditOrderedQuantities={!!deliveryDate && !isSaveOrderLoading}
        orderData={orderData}
        calculateOrder={calculateOrder}
        calculatedOrderResponse={calculatedOrderResponse}
        isPreviewOrderModalOpen={isPreviewOrderModalOpen}
        setIsPreviewOrderModalOpen={setIsPreviewOrderModalOpen}
        completeOrder={handleCompleteOrder}
        savedOrderResponse={savedOrderResponse}
        setOrderComment={setOrderComment}
        step={step}
        setStep={setStep}
      />

      <OrderInfoModal isOpen={isAvailabilityModalOpen} onClose={() => setIsAvailabilityModalOpen(false)} />

      <SuccessModal isOpen={isSuccessModalOpen} onClose={resetOrder} />
    </>
  );
};

export default CreateOrder;
